/**
 * MarkPostedModal — dialog for the manual "Mark as posted" path
 * (spec §5.7.3 · primary posting flow, always available).
 *
 * Client-side host validation runs before we call the API so the tenant
 * gets instant feedback on typos. The API still enforces the check —
 * we're only trying to save a round-trip.
 *
 * The API's `POSTED_URL_HOST_MISMATCH` error code is surfaced inline
 * under the input rather than as a generic toast, so the tenant knows
 * exactly which field to fix.
 */
import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import type { OffPageBriefVariant } from '@seo/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/spinner';
import { ApiError } from '@/lib/api';
import { humanizeApiError } from '@/lib/error-messages';
import {
  canonicalHostForPlatform,
  hostMatchesPlatform,
  PLATFORM_LABELS,
} from '@/features/off-page-briefs/lib/variants';
import { useMarkVariantPosted } from '@/features/off-page-briefs/use-mark-variant-posted';

export interface MarkPostedModalProps {
  opbId: string;
  variant: OffPageBriefVariant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MarkPostedModal({
  opbId,
  variant,
  open,
  onOpenChange,
}: MarkPostedModalProps): JSX.Element {
  const [url, setUrl] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);
  const markPosted = useMarkVariantPosted(opbId);

  // Reset the input every time the modal opens for a different variant so
  // stale URLs don't leak between rows.
  useEffect(() => {
    if (open) {
      setUrl('');
      setClientError(null);
      markPosted.reset();
    }
    // markPosted.reset is stable across renders; safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, variant.id]);

  const canonical = canonicalHostForPlatform(variant.platform);
  const platformLabel = PLATFORM_LABELS[variant.platform];

  const submit = (): void => {
    setClientError(null);
    if (!url.trim()) {
      setClientError('Paste the URL where you posted this.');
      return;
    }
    if (!hostMatchesPlatform(url, variant.platform)) {
      setClientError(
        `That URL doesn't look like a ${platformLabel} link. Expected a ${canonical} URL.`,
      );
      return;
    }
    markPosted.mutate(
      { variantId: variant.id, body: { posted_url: url.trim() } },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  };

  const serverError = markPosted.error;
  const isHostMismatch =
    serverError instanceof ApiError && serverError.code === 'POSTED_URL_HOST_MISMATCH';
  const serverErrorMessage = serverError
    ? isHostMismatch
      ? `That URL host doesn't match ${platformLabel}. Expected ${canonical}.`
      : humanizeApiError(serverError).description
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark as posted</DialogTitle>
          <DialogDescription>
            Paste the {platformLabel} URL where this variant is now live. We use
            it to track outcomes (backlinks, AI citations, referrals).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="posted-url">Posted URL</Label>
          <Input
            id="posted-url"
            type="url"
            placeholder={`https://${canonical}/…`}
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setClientError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            autoFocus
          />
          {clientError || serverErrorMessage ? (
            <div className="flex items-start gap-1.5 text-xs text-destructive">
              <AlertCircle className="mt-0.5 h-3 w-3 flex-none" />
              <span>{clientError ?? serverErrorMessage}</span>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Must be on {canonical}. Subdomains (e.g. regional LinkedIn) are OK.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={markPosted.isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={markPosted.isPending}>
            {markPosted.isPending ? (
              <>
                <Spinner /> Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
